import { useState } from "react";
import { ChevronDown, MapPin } from "react-feather";
import { event, TrackingData } from "@/lib/types";
import { diaFalado, hora } from "@/lib/utils";
import { textoComLinks } from "@/lib/links";

// Histórico do rastreio, como na Amazon e no Shop: separado por dia, a hora
// na frente e o mais recente em cima. Rastreio longo (transportadora tem
// dezenas de eventos) mostra os primeiros e abre o resto num clique.
const VISIVEIS = 5;

export default function Historico({ dados }: { dados: TrackingData }) {
  const [tudo, setTudo] = useState(false);
  const eventos = tudo ? dados.events : dados.events.slice(0, VISIVEIS);
  const escondidos = dados.events.length - VISIVEIS;
  const aoVivo = Boolean(dados.aoVivo);

  const dias: { dia: string; eventos: { ev: event; i: number }[] }[] = [];
  eventos.forEach((ev, i) => {
    const dia = diaFalado(ev.createdAt);
    if (dias[dias.length - 1]?.dia !== dia) dias.push({ dia, eventos: [] });
    dias[dias.length - 1].eventos.push({ ev, i });
  });

  return (
    <section className="rounded-3xl bg-white px-5 py-6 shadow-[0_1px_2px_rgba(63,32,33,0.06)] ring-1 ring-[#EFE6DD] sm:px-8 sm:py-7">
      <h2 className="text-lg font-bold text-[#3F2021]">Histórico</h2>

      {/* Código recém-criado: existe, mas a transportadora ainda não registrou nada. */}
      {dados.events.length === 0 && (
        <p className="mt-3 text-[15px] text-[#6b5556]">Ainda sem movimentação. As atualizações aparecem aqui assim que a transportadora registrar.</p>
      )}

      <div className="mt-4 space-y-5">
        {dias.map(({ dia, eventos: doDia }) => (
          <div key={dia}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#A8978F]">{dia}</p>
            <ol>
              {doDia.map(({ ev, i }, j) => {
                const primeiro = i === 0;
                // A linha liga os eventos do mesmo dia.
                const ultimoDoDia = j === doDia.length - 1;
                return (
                  <li key={i} className="relative grid grid-cols-[3.25rem_1.25rem_1fr] gap-x-2 pb-5 last:pb-0">
                    <time className={`pt-px text-sm tabular-nums ${primeiro ? "font-semibold text-[#3F2021]" : "text-[#8a7778]"}`}>
                      {hora(ev.createdAt)}
                    </time>
                    <div className="relative flex justify-center">
                      {!ultimoDoDia && <span className="absolute top-3 h-[calc(100%+1.25rem)] w-0.5 bg-[#EFE6DD]" aria-hidden="true" />}
                      <span
                        className={`relative mt-1 rounded-full ${
                          primeiro ? "size-3.5 bg-[#DC4405] ring-4 ring-[#FFE3D1]" : "mt-1.5 size-2.5 bg-[#CDBDB0]"
                        }`}
                      />
                    </div>
                    <div className="min-w-0 [overflow-wrap:anywhere]">
                      <p className={`leading-snug ${primeiro ? "font-semibold text-[#3F2021]" : "text-[#5b4546]"}`}>{ev.description}</p>
                      <Detalhe dados={dados} ev={ev} aoVivo={aoVivo} />
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>

      {escondidos > 0 && (
        <button
          type="button"
          onClick={() => setTudo(!tudo)}
          className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#EFE6DD] py-2.5 text-sm font-semibold text-[#3F2021] hover:bg-[#FCFAF7]"
        >
          {tudo ? "Mostrar menos" : `Ver histórico completo (mais ${escondidos})`}
          <ChevronDown size={16} className={tudo ? "rotate-180" : ""} />
        </button>
      )}
    </section>
  );
}

// Onde o pacote estava. A unidade da entrega ao vivo ("Entrega ao vivo ·
// beuni") não ajuda o cliente e fica de fora.
function Detalhe({ dados, ev, aoVivo }: { dados: TrackingData; ev: event; aoVivo: boolean }) {
  const unidade = ev?.currentUnit;
  if (unidade?.address?.address) {
    return (
      <div className="mt-2 w-fit rounded-xl bg-[#FCFAF7] p-3 text-sm text-[#5b4546] ring-1 ring-[#EFE6DD]">
        <p>{unidade.unitName}</p>
        <p className="font-semibold">
          {unidade.address.address}, {unidade.address.number}, {unidade.address.neighborhood} - CEP: {unidade.address.postalCode}
        </p>
        <p className="font-semibold">
          {unidade.address.city} - {unidade.address.state}
        </p>
        <div>{infoAdicional(dados, ev)}</div>
      </div>
    );
  }
  return (
    <>
      {unidade?.unitName && !aoVivo && (
        <p className="mt-0.5 text-sm text-[#8a7778]">
          <MapPin size={12} className="mr-1 inline -translate-y-px" />
          {unidade.unitName}
        </p>
      )}
      {ev?.additionalInfo && <p className="mt-0.5 text-sm text-[#8a7778]">{infoAdicional(dados, ev)}</p>}
    </>
  );
}

const FRASE_RETIRADA =
  "Para retirá-lo, é preciso informar o código do objeto e apresentar documentação que comprove ser o destinatário ou pessoa por ele oficialmente autorizada";
// O código do rastreio entra em negrito no meio da frase, então ela é montada
// em duas partes daqui, sem repetir o texto visível no JSX.
const CORTE = "código do objeto ";
const ANTES_DO_CODIGO = FRASE_RETIRADA.slice(0, FRASE_RETIRADA.indexOf(CORTE) + CORTE.length);
const DEPOIS_DO_CODIGO = ` ${FRASE_RETIRADA.slice(FRASE_RETIRADA.indexOf(CORTE) + CORTE.length)}.`;

function infoAdicional(dados: TrackingData, ev: event) {
  const info = ev?.additionalInfo ?? "";
  const fimDaFrase = info.indexOf(FRASE_RETIRADA);
  if (fimDaFrase >= 0) {
    // Os Correios põem o prazo de retirada depois dessa frase ("Disponível
    // para retirada até ..."). Reescrita a frase com o código em negrito, o
    // que vem depois dela continua aparecendo.
    const codigo = dados.originalTrackingCode;
    const resto = info.slice(fimDaFrase + FRASE_RETIRADA.length).replace(/^[.\s]+/, "");
    return (
      <>
        {ANTES_DO_CODIGO}
        {codigo && <strong>{codigo}</strong>}
        {codigo ? DEPOIS_DO_CODIGO : DEPOIS_DO_CODIGO.trimStart()}
        {resto && <> {textoComLinks(resto)}</>}
      </>
    );
  }
  return textoComLinks(info);
}
